import { useState, useEffect, useCallback } from 'react';
import { apiJson, apiFetch } from '../../lib/api';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

interface OrgEntry {
  id: string;
  slug: string;
  display_name: string;
  compute_mode: string;
  member_count: number;
  created_at: string;
}

interface MemberEntry {
  user_id: string;
  login: string;
  org_role: string;
  joined_at: string;
  avatar_url: string | null;
}

export function OrgManager() {
  const [orgs, setOrgs] = useState<OrgEntry[]>([]);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [members, setMembers] = useState<MemberEntry[]>([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newCompute, setNewCompute] = useState('shared');
  const [newCostLimit, setNewCostLimit] = useState('');
  const [newMaxMembers, setNewMaxMembers] = useState('');
  const [inviteLogin, setInviteLogin] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteUrl, setInviteUrl] = useState('');
  const [error, setError] = useState('');

  const loadOrgs = useCallback(() => {
    apiJson<{ orgs: OrgEntry[] }>('/api/admin/orgs')
      .then(d => setOrgs(d.orgs || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const loadMembers = useCallback((orgId: string) => {
    apiJson<{ members: MemberEntry[] }>(`/api/admin/orgs/${orgId}/members`)
      .then(d => setMembers(d.members || []))
      .catch(() => {});
  }, []);

  const toggleExpand = (orgId: string) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      loadMembers(orgId);
      setInviteUrl('');
      setInviteLogin('');
    }
  };

  const createOrg = async () => {
    setError('');
    const body: Record<string, unknown> = { display_name: newName };
    if (newSlug) body.slug = newSlug;
    if (newCompute) body.compute_mode = newCompute;
    if (newCostLimit) body.max_monthly_cost_usd = parseFloat(newCostLimit);
    if (newMaxMembers) body.max_members = parseInt(newMaxMembers);

    const resp = await apiFetch('/api/admin/orgs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (data.ok) {
      setShowNewForm(false);
      setNewName('');
      setNewSlug('');
      loadOrgs();
    } else {
      setError(data.error || 'Failed to create org');
    }
  };

  const deleteOrg = async (id: string) => {
    if (!confirm(`Delete organization "${id}"?`)) return;
    await apiFetch(`/api/admin/orgs/${id}`, { method: 'DELETE' });
    setExpandedOrg(null);
    loadOrgs();
  };

  const inviteMember = async (orgId: string) => {
    setError('');
    const resp = await apiFetch(`/api/admin/orgs/${orgId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ github_login: inviteLogin, org_role: inviteRole }),
    });
    const data = await resp.json();
    if (data.ok) {
      setInviteUrl(data.invite_url || '');
      setInviteLogin('');
    } else {
      setError(data.error || 'Failed to send invite');
    }
  };

  const removeMember = async (orgId: string, userId: string) => {
    if (!confirm('Remove this member?')) return;
    await apiFetch(`/api/admin/orgs/${orgId}/members/${userId}`, { method: 'DELETE' });
    loadMembers(orgId);
    loadOrgs();
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 4,
    color: '#f8f8f2',
    fontSize: 13,
    fontFamily: FONT,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'all 0.15s ease',
  };

  const btnStyle: React.CSSProperties = {
    padding: '8px 16px',
    background: '#50fa7b',
    color: '#0d0d0d',
    border: 'none',
    borderRadius: 4,
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };

  const btnGhost: React.CSSProperties = {
    padding: '8px 16px',
    background: 'transparent',
    color: '#a8b2b8',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 4,
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: 400,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 11, color: '#6272a4', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {orgs.length} organization{orgs.length !== 1 ? 's' : ''}
        </span>
        <button onClick={() => setShowNewForm(!showNewForm)} style={btnStyle}>
          + New Org
        </button>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', background: 'rgba(255,85,85,0.08)', border: '1px solid rgba(255,85,85,0.2)',
          borderRadius: 8, color: '#ff5555', fontSize: 12, marginBottom: 16, fontFamily: FONT,
        }}>
          {error}
        </div>
      )}

      {/* New org form */}
      {showNewForm && (
        <div style={{
          background: '#282a36', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
          padding: 24, marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 16 }}>
            Create Organization
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 10, color: '#6272a4', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Display Name *</label>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Acme Corp"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#6272a4', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Slug (optional)</label>
              <input
                value={newSlug}
                onChange={e => setNewSlug(e.target.value)}
                placeholder="acme-corp"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#6272a4', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Compute Mode</label>
              <select
                value={newCompute}
                onChange={e => setNewCompute(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="shared">Shared</option>
                <option value="dedicated">Dedicated</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#6272a4', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cost Limit (USD/mo)</label>
              <input
                value={newCostLimit}
                onChange={e => setNewCostLimit(e.target.value)}
                placeholder="500"
                type="number"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={createOrg} disabled={!newName} style={{ ...btnStyle, opacity: newName ? 1 : 0.5 }}>
              Create
            </button>
            <button onClick={() => setShowNewForm(false)} style={btnGhost}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Org list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {orgs.map(org => (
          <div key={org.id} style={{
            background: '#1e1e2e', border: `1px solid ${expandedOrg === org.id ? 'rgba(80,250,123,0.25)' : 'rgba(255,255,255,0.06)'}`,
            borderRadius: 8, overflow: 'hidden', transition: 'all 0.15s ease',
          }}>
            {/* Row header */}
            <div
              onClick={() => toggleExpand(org.id)}
              style={{
                padding: '14px 16px',
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div>
                <div style={{ fontWeight: 600, color: '#f8f8f2', fontSize: 13 }}>{org.display_name}</div>
                <div style={{ fontSize: 11, color: '#6272a4', marginTop: 2 }}>{org.slug}</div>
              </div>
              <span style={{
                fontSize: 10, color: '#50fa7b', padding: '2px 8px',
                background: 'rgba(80,250,123,0.08)', borderRadius: 4, justifySelf: 'start',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {org.compute_mode}
              </span>
              <span style={{ fontSize: 11, color: '#a8b2b8' }}>{org.member_count} members</span>
              <span style={{ fontSize: 11, color: '#6272a4', justifySelf: 'end' }}>
                {new Date(org.created_at).toLocaleDateString()}
              </span>
            </div>

            {/* Expanded detail */}
            {expandedOrg === org.id && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: 16 }}>
                {/* Members table */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 12 }}>Members</div>
                  {members.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <th style={{ textAlign: 'left', padding: '8px 8px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>User</th>
                          <th style={{ textAlign: 'left', padding: '8px 8px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</th>
                          <th style={{ textAlign: 'left', padding: '8px 8px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Joined</th>
                          <th style={{ textAlign: 'right', padding: '8px 8px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(m => (
                          <tr key={m.user_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                            <td style={{ padding: '8px', fontSize: 13, color: '#f8f8f2' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {m.avatar_url && (
                                  <img src={m.avatar_url} width={20} height={20} style={{ borderRadius: '50%' }} />
                                )}
                                @{m.login}
                              </span>
                            </td>
                            <td style={{ padding: '8px', fontSize: 11, color: '#bd93f9' }}>{m.org_role}</td>
                            <td style={{ padding: '8px', fontSize: 11, color: '#6272a4' }}>
                              {new Date(m.joined_at).toLocaleDateString()}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <button
                                onClick={() => removeMember(org.id, m.user_id)}
                                style={{
                                  padding: '4px 10px', background: 'transparent',
                                  border: '1px solid rgba(255,85,85,0.3)', borderRadius: 4,
                                  color: '#ff5555', fontSize: 11, fontFamily: FONT, cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ fontSize: 11, color: '#6272a4', fontFamily: FONT }}>No members yet</p>
                  )}
                </div>

                {/* Invite form */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 12 }}>Invite Member</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 10, color: '#6272a4', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>GitHub Login</label>
                      <input
                        value={inviteLogin}
                        onChange={e => setInviteLogin(e.target.value)}
                        placeholder="username"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ width: 120 }}>
                      <label style={{ fontSize: 10, color: '#6272a4', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</label>
                      <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    </div>
                    <button
                      onClick={() => inviteMember(org.id)}
                      disabled={!inviteLogin}
                      style={{ ...btnStyle, opacity: inviteLogin ? 1 : 0.5, whiteSpace: 'nowrap' }}
                    >
                      Send Invite
                    </button>
                  </div>
                  {inviteUrl && (
                    <div style={{
                      marginTop: 8, padding: '8px 12px', background: 'rgba(80,250,123,0.06)',
                      border: '1px solid rgba(80,250,123,0.2)', borderRadius: 8, fontSize: 11, color: '#50fa7b',
                      wordBreak: 'break-all', fontFamily: FONT,
                    }}>
                      Invite link: {inviteUrl}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => deleteOrg(org.id)} style={{
                    padding: '6px 16px', background: 'transparent',
                    border: '1px solid rgba(255,85,85,0.3)', borderRadius: 4,
                    color: '#ff5555', fontSize: 12, fontFamily: FONT, cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}>
                    Delete Organization
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {orgs.length === 0 && !showNewForm && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6272a4', fontSize: 13, fontFamily: FONT }}>
            No organizations yet. Click "+ New Org" to create one.
          </div>
        )}
      </div>
    </div>
  );
}
